import { useEffect, useRef, useState } from 'react'
import { API_BASE } from '@/lib/config'

function loadScript(src: string, reload = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (reload) {
        existing.remove()
        delete window.clyrCareersAdmin
      } else {
        if ((existing as HTMLScriptElement).dataset.clyrLoaded === 'true') {
          resolve()
        } else {
          existing.addEventListener('load', () => resolve(), { once: true })
          existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true })
        }
        return
      }
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => {
      s.dataset.clyrLoaded = 'true'
      resolve()
    }
    s.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.body.appendChild(s)
  })
}

export function CareersAdminHost({ tab = 'careers' }: { tab?: string }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const mountId = useRef(0)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [loadError, setLoadError] = useState('')
  const [retryNonce, setRetryNonce] = useState(0)

  useEffect(() => {
    window.CLYR_API_BASE = API_BASE
  }, [])

  useEffect(() => {
    const id = ++mountId.current
    let cancelled = false

    async function init() {
      if (!rootRef.current) return
      setLoadState('loading')
      setLoadError('')
      try {
        const res = await fetch('/legacy/careers-admin-shell.html')
        if (!res.ok) throw new Error(`Admin layout request failed (${res.status}).`)
        const html = await res.text()
        if (cancelled || !rootRef.current || mountId.current !== id) return

        rootRef.current.innerHTML = `
          <div id="careers-admin-loading" hidden></div>
          <div id="careers-admin-denied" hidden></div>
          <div class="careers-admin-shell-root">${html}</div>
          <div id="admin-hub-tabs" hidden>
            <button type="button" class="admin-hub-tab" data-tab="careers" id="tab-careers"></button>
            <button type="button" class="admin-hub-tab" data-tab="staff" id="tab-staff"></button>
            <button type="button" class="admin-hub-tab" data-tab="roadmap" id="tab-roadmap"></button>
            <button type="button" class="admin-hub-tab" data-tab="themes" id="tab-themes"></button>
          </div>
          <a href="#" id="nav-logout" hidden></a>
          <a href="#" id="nav-trust-safety" hidden></a>
          <a href="#" id="nav-login" hidden></a>
        `

        await loadScript('/scripts/careers-admin.js', true)
        if (cancelled || mountId.current !== id) return
        if (!window.clyrCareersAdmin) throw new Error('The careers module did not initialize.')

        window.clyrCareersAdmin.showAdminTab(tab)
        setLoadState('ready')
      } catch (error) {
        if (cancelled || mountId.current !== id) return
        setLoadError(error instanceof Error ? error.message : 'The admin module could not be loaded.')
        setLoadState('error')
      }
    }

    void init()
    return () => {
      cancelled = true
    }
  }, [tab, retryNonce])

  return (
    <div className="admin-legacy-module">
      {loadState !== 'ready' && (
        <div className="admin-module-state" role={loadState === 'error' ? 'alert' : 'status'}>
          {loadState === 'loading' ? (
            <><span className="admin-module-spinner" aria-hidden="true" /><p>Loading admin tools…</p></>
          ) : (
            <>
              <strong>Couldn’t load this admin tool</strong>
              <p>{loadError}</p>
              <button type="button" onClick={() => setRetryNonce((value) => value + 1)}>Try again</button>
            </>
          )}
        </div>
      )}
      <div ref={rootRef} className="admin-legacy-host clyr-theme careers-main" />
    </div>
  )
}
